import { Injectable } from '@angular/core';

import { HlsUtilService } from './HlsUtil.Service';
import { IVbSource } from './IVbSource';
import { userAgentUtil } from './UserAgentUtil';

export type NativePlayBlacklistTest = (source: IVbSource, userAgent?: string) => boolean;

@Injectable()
export class NativePlaybackUtilService {
	private readonly DEFAULT_Q_VALUE: number = 80000;
	private readonly ORIGINAL_INSTANCE_Q_VALUE: number = 90000;
	private readonly WIN7_IE11_MAX_MP4_Q_VALUE: number = 1080;

	private videoElement: HTMLVideoElement;

	constructor(
		private HlsUtilService: HlsUtilService
	) {
		this.videoElement = document.createElement('video');
	}

	public canPlayNatively(source: IVbSource): boolean {
		if (!this.videoElement.canPlayType) {
			return false;
		}

		const canPlay: string = this.videoElement.canPlayType(source.type);

		return (canPlay === 'probably' || canPlay === 'maybe') &&
			!this.getNativePlayBlacklist()
				.reduce((accumulator: boolean, currentTest) => accumulator || currentTest(source), false);
	}

	public getNativePlayBlacklist(): NativePlayBlacklistTest[] {
		return [
			(source: IVbSource) => this.isChromeWithMediaSourceForHls(source),
			(source: IVbSource) => this.isAndroidChromeUnsupportedHls(source),
			(source: IVbSource) => this.isUnsupportedMixedContentPlayback(source),
			(source: IVbSource) => this.isLegacyEdgeHls(source),
			(source: IVbSource) => this.isSafariUnsupportedHls(source),
			(source: IVbSource) => this.isWin7IeUnsupportedResolution(source),
			(source: IVbSource) => this.HlsUtilService.isRevConnect(source),
			(source: IVbSource) => this.isSafariWithManagedMediaSourceForHls(source)
		];
	}

	public isWin7IeUnsupportedResolution(source: IVbSource): boolean {
		const isExceedsMax: boolean = source.qValue > this.WIN7_IE11_MAX_MP4_Q_VALUE;
		const isDefault: boolean = source.qValue === this.DEFAULT_Q_VALUE;
		const isOriginal: boolean = source.qValue === this.ORIGINAL_INSTANCE_Q_VALUE;
		const isUnsupportedQValue: boolean = source.is360Enabled ?
			isExceedsMax :
			isExceedsMax && !isOriginal && !isDefault;

		return userAgentUtil.isIe &&
			userAgentUtil.isWindows7 &&
			source.type === 'video/mp4' &&
			isUnsupportedQValue;
	}

	public isUnsupportedMixedContentPlayback(source: IVbSource): boolean {
		// legacy Edge does not support native mixed content playback
		return userAgentUtil.isLegacyEdge && !source.src.startsWith(location.protocol); //legacy Edge AND protocol of src differs from that of the window
	}

	private isAndroidChromeUnsupportedHls(source: IVbSource): boolean {
		// non-mixed content HLS on Android Chrome
		return userAgentUtil.isAndroidChrome && this.HlsUtilService.isHLS(source.src, source.type) && !this.HlsUtilService.isMixedContent(source.src);
	}

	private isLegacyEdgeHls(source: IVbSource): boolean {
		// native HLS on legacy Edge
		return userAgentUtil.isLegacyEdge && this.HlsUtilService.isHLS(source.src, source.type);
	}

	private isSafariUnsupportedHls(source: IVbSource): boolean {
		return userAgentUtil.isSafariMac && this.HlsUtilService.isHLS(source.src, source.type) && !this.HlsUtilService.isMixedContent(source.src);
	}

	private isChromeWithMediaSourceForHls(source: IVbSource): boolean {
		return userAgentUtil.isChrome && !!(window as any).MediaSource && this.HlsUtilService.isHLS(source.src, source.type) && !this.HlsUtilService.isMixedContent(source.src);
	}

	/**
	 * iOS and ipadOS 17 started supprting ManagedMediaSource for HLS.
	 * but added a check for safari only as it is the only browser that supports ManagedMediaSource.
	 * can't add iOS check because ipadOS is not correct.
	 *
	 */
	private isSafariWithManagedMediaSourceForHls(source: IVbSource): boolean {
		return userAgentUtil.isSafari && !!(window as any).ManagedMediaSource && this.HlsUtilService.isHLS(source.src, source.type) && !this.HlsUtilService.isMixedContent(source.src);
	}
}
